import { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useAppSelector } from 'store/hook';
import type { TableInsightItem } from 'types/ml-insights.types';
import { isHiddenInsightType } from 'types/ml-insights.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const Cards = ({ enableDarkTheme }: { enableDarkTheme: boolean }) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    data: { insights },
    dateRange,
  } = useAppSelector((state) => state.mlInsightsReducer);

  const { numOfAssets, numOfNewInsights, numOfInsightsRequiringAction } =
    useMemo(() => {
      const getInsightStart = (insight: TableInsightItem) =>
        insight.alertStartTs ?? insight.timestamp ?? '';

      const filteredInsights = insights.filter((insight) => {
        // Hidden insight types are excluded from the listing, so they must not
        // be counted in these tiles either.
        if (isHiddenInsightType(insight as TableInsightItem)) {
          return false;
        }

        const insightDate = new Date(
          getInsightStart(insight as TableInsightItem)
        );
        const startDate = dateRange.startDate
          ? new Date(dateRange.startDate)
          : null;
        const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;

        if (!startDate && !endDate) {
          return true;
        }

        if (startDate) {
          startDate.setHours(0, 0, 0, 0);
        }
        if (endDate) {
          endDate.setHours(23, 59, 59, 999);
        }

        const isAfterStart = !startDate || insightDate >= startDate;
        const isBeforeEnd = !endDate || insightDate <= endDate;

        return isAfterStart && isBeforeEnd;
      });

      // 1. No. of Assets i.e Number of unique assets 4 which at least one insight or anomaly was detected
      const uniqueDevices = new Set(
        filteredInsights.map((insight) => insight.device)
      );
      const numOfAssets = uniqueDevices.size;

      // 2. New Insights i.e Total number of unique insights generated across all assets
      const uniqueInsights = new Set(
        filteredInsights.map(
          (insight) =>
            `${insight.insight}-${insight.device}-${getInsightStart(
              insight as TableInsightItem
            )}`
        )
      );
      const numOfNewInsights = uniqueInsights.size;

      // 3. Active notifications in the selected date range (same rule as table "Active" view)
      const numOfInsightsRequiringAction = filteredInsights.filter(
        (insight) => {
          const row = insight as TableInsightItem;
          const resolution = row?.resolution;
          const endTsRaw = row.alertEndTs;
          const hasEndTs =
            typeof endTsRaw === 'string'
              ? endTsRaw.trim().length > 0
              : Boolean(endTsRaw);
          return resolution === 'Active' && !hasEndTs;
        }
      ).length;

      return {
        numOfAssets,
        numOfNewInsights,
        numOfInsightsRequiringAction,
      };
    }, [insights, dateRange.startDate, dateRange.endDate]);

  const cards = [
    {
      title: t('assets'),
      value: numOfAssets,
    },
    {
      title: t('number_of_insights'),
      value: numOfNewInsights,
    },
    {
      title: t('insights_requiring_actions'),
      value: numOfInsightsRequiringAction,
    },
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        {cards.map((item, index) => {
          return (
            <Grid item lg={4} sm={12} md={4} xs={12} key={index}>
              <Box
                sx={{
                  padding: '20px 16px',
                  backgroundColor: palette.boxBackground,
                  borderRadius: '5px',
                  border: `1px solid rgba(255, 255, 255, 0.15)`,
                  minHeight: '80px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: 1.3,
                    opacity: 0.85,
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '30px',
                    fontWeight: 700,
                    // lineHeight 1 keeps the taller value from adding dead
                    // space and growing the card past its original height.
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    marginTop: '10px',
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
